"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createApiKeyAccount = exports.createAccount = void 0;
const viem_1 = require("viem");
const accounts_1 = require("viem/accounts");
const viem_2 = require("viem");
const http_1 = require("@turnkey/http");
const api_key_stamper_1 = require("@turnkey/api-key-stamper");
async function createAccount(input) {
    const { client, organizationId, privateKeyId } = input;
    let { ethereumAddress } = input;
    // Fetch the address if we don't have it
    if (ethereumAddress === undefined) {
        const data = await client.getPrivateKey({
            privateKeyId: privateKeyId,
            organizationId: organizationId,
        });
        ethereumAddress = data.privateKey.addresses.find((item) => item.format === "ADDRESS_FORMAT_ETHEREUM")?.address;
        if (typeof ethereumAddress !== "string" || !ethereumAddress) {
            throw new http_1.TurnkeyActivityError({
                message: `Unable to find Ethereum address for key ${privateKeyId} under organization ${organizationId}`,
            });
        }
    }
    return (0, accounts_1.toAccount)({
        address: ethereumAddress,
        signMessage: function ({ message, }) {
            return signMessage(client, message, organizationId, privateKeyId);
        },
        signTransaction: function (transaction, args) {
            const serializer = !args?.serializer
                ? viem_1.serializeTransaction
                : args.serializer;
            return signTransaction(client, transaction, serializer, organizationId, privateKeyId);
        },
        signTypedData: function (typedData) {
            return signTypedData(client, typedData, organizationId, privateKeyId);
        },
    });
}
exports.createAccount = createAccount;
/**
 * Creates a new Custom Account backed by a Turnkey API key.
 * @deprecated use {@link createAccount} instead.
 */
async function createApiKeyAccount(config) {
    const { apiPublicKey, apiPrivateKey, baseUrl, organizationId, privateKeyId } = config;
    const stamper = new api_key_stamper_1.ApiKeyStamper({
        apiPublicKey: apiPublicKey,
        apiPrivateKey: apiPrivateKey,
    });
    const client = new http_1.TurnkeyClient({
        baseUrl: baseUrl,
    }, stamper);
    const data = await client.getPrivateKey({
        privateKeyId: privateKeyId,
        organizationId: organizationId,
    });
    const ethereumAddress = data.privateKey.addresses.find((item) => item.format === "ADDRESS_FORMAT_ETHEREUM")?.address;
    if (typeof ethereumAddress !== "string" || !ethereumAddress) {
        throw new http_1.TurnkeyActivityError({
            message: `Unable to find Ethereum address for key ${privateKeyId} under organization ${organizationId}`,
        });
    }
    return (0, accounts_1.toAccount)({
        address: ethereumAddress,
        signMessage: function ({ message, }) {
            return signMessage(client, message, organizationId, privateKeyId);
        },
        signTransaction: function (transaction, args) {
            const serializer = !args?.serializer
                ? viem_1.serializeTransaction
                : args.serializer;
            return signTransaction(client, transaction, serializer, organizationId, privateKeyId);
        },
        signTypedData: function (typedData) {
            return signTypedData(client, typedData, organizationId, privateKeyId);
        },
    });
}
exports.createApiKeyAccount = createApiKeyAccount;
async function signMessage(client, message, organizationId, privateKeyId) {
    const hashedMessage = (0, viem_2.hashMessage)(message);
    const signedMessage = await signMessageWithErrorWrapping(client, hashedMessage, organizationId, privateKeyId);
    return `${signedMessage}`;
}
async function signTransaction(client, transaction, serializer, organizationId, privateKeyId) {
    const serializedTx = serializer(transaction);
    const nonHexPrefixedSerializedTx = serializedTx.replace(/^0x/, "");
    return await signTransactionWithErrorWrapping(client, nonHexPrefixedSerializedTx, organizationId, privateKeyId);
}
async function signTypedData(client, data, organizationId, privateKeyId) {
    const hashToSign = (0, viem_1.hashTypedData)(data);
    return await signMessageWithErrorWrapping(client, hashToSign, organizationId, privateKeyId);
}
async function signTransactionWithErrorWrapping(client, unsignedTransaction, organizationId, privateKeyId) {
    let signedTx;
    try {
        signedTx = await signTransactionImpl(client, unsignedTransaction, organizationId, privateKeyId);
    }
    catch (error) {
        if (error instanceof http_1.TurnkeyActivityError) {
            throw error;
        }
        throw new http_1.TurnkeyActivityError({
            message: `Failed to sign transaction: ${error.message}`,
            cause: error,
        });
    }
    return `0x${signedTx}`;
}
async function signTransactionImpl(client, unsignedTransaction, organizationId, privateKeyId) {
    const { activity } = await client.signTransaction({
        type: "ACTIVITY_TYPE_SIGN_TRANSACTION_V2",
        organizationId: organizationId,
        parameters: {
            signWith: privateKeyId,
            type: "TRANSACTION_TYPE_ETHEREUM",
            unsignedTransaction: unsignedTransaction,
        },
        timestampMs: String(Date.now()), // millisecond timestamp
    });
    const { id, status, type } = activity;
    if (activity.status === "ACTIVITY_STATUS_COMPLETED") {
        return assertNonNull(activity?.result?.signTransactionResult?.signedTransaction);
    }
    throw new http_1.TurnkeyActivityError({
        message: `Invalid activity status: ${activity.status}`,
        activityId: id,
        activityStatus: status,
        activityType: type,
    });
}
async function signMessageWithErrorWrapping(client, message, organizationId, privateKeyId) {
    let signedMessage;
    try {
        signedMessage = await signMessageImpl(client, message, organizationId, privateKeyId);
    }
    catch (error) {
        if (error instanceof http_1.TurnkeyActivityError) {
            throw error;
        }
        throw new http_1.TurnkeyActivityError({
            message: `Failed to sign: ${error.message}`,
            cause: error,
        });
    }
    return signedMessage;
}
async function signMessageImpl(client, message, organizationId, privateKeyId) {
    const { activity } = await client.signRawPayload({
        type: "ACTIVITY_TYPE_SIGN_RAW_PAYLOAD_V2",
        organizationId: organizationId,
        parameters: {
            signWith: privateKeyId,
            payload: message,
            encoding: "PAYLOAD_ENCODING_HEXADECIMAL",
            hashFunction: "HASH_FUNCTION_NO_OP",
        },
        timestampMs: String(Date.now()), // millisecond timestamp
    });
    const { id, status, type } = activity;
    if (activity.status === "ACTIVITY_STATUS_COMPLETED") {
        let result = assertNonNull(activity?.result?.signRawPayloadResult);
        let assembled = (0, viem_1.signatureToHex)({
            r: `0x${result.r}`,
            s: `0x${result.s}`,
            v: result.v === "00" ? 27n : 28n,
        });
        // Assemble the hex
        return assertNonNull(assembled);
    }
    throw new http_1.TurnkeyActivityError({
        message: `Invalid activity status: ${activity.status}`,
        activityId: id,
        activityStatus: status,
        activityType: type,
    });
}
function assertNonNull(input) {
    if (input == null) {
        throw new Error(`Got unexpected ${JSON.stringify(input)}`);
    }
    return input;
}
//# sourceMappingURL=index.js.map